import React, { PropsWithChildren } from 'react';
import { IconPlus } from '@tabler/icons-react';

export enum MESSAGE_BOX_STATE {
  SUCCESS,
  ERROR,
  INFO,
  APP_BANNER,
}

export type MessageBoxState = (typeof MESSAGE_BOX_STATE)[keyof typeof MESSAGE_BOX_STATE];

const stateStyles = {
  [MESSAGE_BOX_STATE.SUCCESS]: {
    bgColor: 'bg-success-200',
    borderColor: 'border-success',
    textColor: 'text-neutral',
  },
  [MESSAGE_BOX_STATE.ERROR]: {
    bgColor: 'bg-error',
    borderColor: 'border-error-400',
    textColor: 'text-neutral',
  },
  [MESSAGE_BOX_STATE.INFO]: {
    bgColor: 'bg-background',
    borderColor: 'border-dark-100',
    textColor: 'text-dark',
  },
  [MESSAGE_BOX_STATE.APP_BANNER]: {
    bgColor: 'bg-neutral',
    borderColor: 'border-primary',
    textColor: 'text-dark',
  },
} as const;

interface MessageBoxInput {
  state?: MessageBoxState;
  message?: string;
  className?: string;
  onClose: () => void;
}

export const MessageBox: React.FC<MessageBoxInput & PropsWithChildren> = ({
  state = MESSAGE_BOX_STATE.SUCCESS,
  message,
  className = '',
  children,
  onClose,
}) => {
  const { bgColor, borderColor, textColor } = stateStyles[state];

  return (
    <div
      className={`flex items-center gap-4 rounded-lg border-2 px-4 py-3 font-bold md:px-6 md:py-4 ${borderColor} ${bgColor} ${textColor} ${className}`}
      role="alert"
      aria-live={state === MESSAGE_BOX_STATE.INFO ? 'polite' : 'assertive'}
      aria-atomic="true"
    >
      <CloseButton onClick={onClose} />
      {children ? <div className="w-full">{children}</div> : <span className="mt-[3px]">{message}</span>}
    </div>
  );
};

const CloseButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <div className="flex items-center justify-center">
    <button onClick={onClick} aria-label="Close notification" className="rounded-full border-2 border-white">
      <IconPlus className="rotate-45" size={24} />
    </button>
  </div>
);
